import React, { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Grid, Button } from '@mui/material';
import { styled } from '@mui/material/styles';
import { useAppData } from '../../../../../hooks/AppDataContext';
import { getGalleries } from '../../../../../services/galleryService';
import { GalleryCard, GalleryCardSkeleton } from './GalleryCard';
import SearchBar from './SearchBar';
import { AddRounded } from '@mui/icons-material';
import CreateGallery from './CreateGallery';
import GalleriesPlaceholder from './GalleriesPlaceholder/GalleriesPlaceholder';

const Root = styled(Grid)(({ theme }) => ({
	height: '100%',
	display: 'flex',
	flexDirection: 'column'
}));

const ActionsContainer = styled(Grid)(({ theme }) => ({
	width: '100%',
	padding: '30px 20px 0',
	display: 'flex',
	justifyContent: 'space-between',
	gap: '20px',
	[theme.breakpoints.down('sm')]: {
		alignItems: 'center',
		flexDirection: 'column-reverse',
		gap: '13px'
	}
}));

const GallerySearch = styled(SearchBar)(({ theme }) => ({
	[theme.breakpoints.down('md')]: {
		marginLeft: 10
	},
	[theme.breakpoints.down('sm')]: {
		width: 'calc(100vw - 65px)',
		marginLeft: 5
	}
}));

const AddGalleryButton = styled(Button)(({ theme }) => ({
	padding: '6px 34px 6px 22px',
	borderRadius: 20,
	textTransform: 'none',
	gap: '8px',
	'&:disabled': {
		backgroundColor: theme.palette.primary.light,
		color: theme.palette.text.contrast,
		opacity: 0.7
	},
	[theme.breakpoints.down('sm')]: {
		width: 'calc(100vw - 65px)',
		marginLeft: 5
	}
}));

const GalleryCardsWrapper = styled(Grid)(({ theme }) => ({
	display: 'flex',
	gap: '19px',
	display: 'flex',
	padding: 20,
	flexWrap: 'wrap',
	flex: 1
}));

function Galleries() {
	const { galleries, setGalleries } = useAppData();
	const [searchParams, setSearchParams] = useSearchParams();
	const [search, setSearch] = useState('');

	const galleryData = galleries?.data?.filter(
		(g) =>
			g.title?.toLowerCase()?.includes(search?.toLowerCase()) ||
			g.description?.toLowerCase().includes(search?.toLowerCase())
	);

	const fetchGalleries = async () => {
		const timestamp = Date.now();

		if (timestamp - galleries.loadedAt > 10 * 60 * 1000) {
			setGalleries({ ...galleries, loading: true });
			const data = await getGalleries();
			setGalleries({ data, loadedAt: Date.now(), loading: false });
		}
	};

	useEffect(() => {
		fetchGalleries();
	}, []);

	return (
		<Root>
			<ActionsContainer>
				<GallerySearch value={search} setValue={setSearch} />
				<AddGalleryButton
					variant="contained"
					disableElevation
					onClick={() => setSearchParams({ create: 'new' })}
				>
					<AddRounded />
					Add Gallery
				</AddGalleryButton>
			</ActionsContainer>
			<GalleryCardsWrapper>
				{!galleries.loading &&
					galleryData?.length > 0 &&
					galleryData.map((gallery) => (
						<GalleryCard
							title={gallery.title}
							description={gallery.description}
							img={gallery.thumbnail}
							galleryID={gallery._id}
							status={gallery?.status}
							key={gallery._id}
						/>
					))}
				{!galleries.loading && galleryData?.length === 0 && <GalleriesPlaceholder />}
				{galleries.loading && [1, 2, 3].map((i) => <GalleryCardSkeleton key={i} />)}
			</GalleryCardsWrapper>
			<CreateGallery
				open={!!searchParams.get('create') && !galleries.loading}
				onClose={() => setSearchParams({})}
			/>
		</Root>
	);
}

export default Galleries;
